import type { AudioTransportState, SttTransportState } from "../../shared/types.ts";
import type { RoleAuth } from "./auth.ts";
import { createAudioIngressSocket } from "./audioIngressSocket.ts";

export interface AudioInputDevice {
  deviceId: string;
  label: string;
}

export interface AudioCaptureSnapshot {
  micActive: boolean;
  micPaused: boolean;
  micLevel: number;
  selectedInputDeviceId: string | null;
  selectedInputDeviceLabel: string | null;
  audioTransport: AudioTransportState;
  audioTransportError: string | null;
  sttTransport: SttTransportState;
  sttError: string | null;
  lastSttActivityAt: number | null;
  lastTranscriptAt: number | null;
  sampleRate: number | null;
}

export interface StartCaptureOptions {
  sessionId: string;
  deviceId: string | null;
  ingressUrl: string;
  auth: RoleAuth;
}

export type MicrophonePermissionState = "granted" | "prompt" | "denied" | "unknown";
export type AudioCaptureListener = (snapshot: AudioCaptureSnapshot) => void;

const WORKLET_URL = "/worklets/pcm16-capture-processor.js";

export async function listAudioInputDevices(): Promise<AudioInputDevice[]> {
  ensureMediaDevices();
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((device) => device.kind === "audioinput")
    .map((device, index) => ({
      deviceId: device.deviceId,
      label: device.label || `Input ${index + 1}`,
    }));
}

export async function requestAudioInputPermission(): Promise<void> {
  ensureMediaDevices();
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach((track) => track.stop());
}

export async function getMicrophonePermissionState(): Promise<MicrophonePermissionState> {
  try {
    const permissionsApi = (navigator as Navigator & {
      permissions?: {
        query(input: { name: "microphone" }): Promise<{ state: string }>;
      };
    }).permissions;
    if (!permissionsApi?.query) return "unknown";
    const result = await permissionsApi.query({ name: "microphone" });
    if (result.state === "granted" || result.state === "prompt" || result.state === "denied") {
      return result.state;
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

export function createAudioCaptureController() {
  const listeners = new Set<AudioCaptureListener>();
  let snapshot: AudioCaptureSnapshot = initialSnapshot();
  let stream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let analyser: AnalyserNode | null = null;
  let workletNode: AudioWorkletNode | null = null;
  let silentGain: GainNode | null = null;
  let ingressSocket: ReturnType<typeof createAudioIngressSocket> | null = null;
  let raf: number | null = null;
  let selectedDevice: AudioInputDevice | null = null;
  let paused = false;

  function subscribe(listener: AudioCaptureListener): () => void {
    listeners.add(listener);
    listener(snapshot);
    return () => listeners.delete(listener);
  }

  function emit(partial: Partial<AudioCaptureSnapshot>): void {
    snapshot = { ...snapshot, ...partial };
    for (const listener of listeners) listener(snapshot);
  }

  async function start(options: StartCaptureOptions): Promise<void> {
    await stop();
    ensureMediaDevices();
    emit({
      audioTransport: "connecting",
      audioTransportError: null,
      sttTransport: "connecting",
      sttError: null,
      selectedInputDeviceId: options.deviceId,
    });

    try {
      selectedDevice = await resolveSelectedDevice(options.deviceId);
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          ...(options.deviceId ? { deviceId: { exact: options.deviceId } } : {}),
          channelCount: { ideal: 1 },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });

      audioContext = newAudioContext();
      await audioContext.audioWorklet.addModule(WORKLET_URL);
      source = audioContext.createMediaStreamSource(stream);
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      workletNode = new AudioWorkletNode(audioContext, "pcm16-capture");
      silentGain = audioContext.createGain();
      silentGain.gain.value = 0;
      workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        if (paused) return;
        ingressSocket?.sendAudio(event.data);
      };

      source.connect(analyser);
      source.connect(workletNode);
      workletNode.connect(silentGain);
      silentGain.connect(audioContext.destination);

      ingressSocket = createAudioIngressSocket({
        ingressUrl: options.ingressUrl,
        sessionId: options.sessionId,
        auth: options.auth,
        sampleRate: audioContext.sampleRate,
        onStatus: ({ audioTransport, audioTransportError }) => emit({ audioTransport, audioTransportError }),
        onMessage: (data) => handleSocketMessage(data),
        onFatalError: (message) => void failCapture(message),
      });
      ingressSocket.start();

      emit({
        micActive: true,
        micPaused: false,
        selectedInputDeviceId: selectedDevice?.deviceId ?? options.deviceId,
        selectedInputDeviceLabel: selectedDevice?.label ?? null,
        sampleRate: audioContext.sampleRate,
      });
      startLevelLoop();
    } catch (err) {
      await stop();
      emitTransportError(err instanceof Error ? err.message : String(err));
      throw err;
    }
  }

  function pause(): void {
    if (!stream || !ingressSocket?.isConnected() || paused) return;
    paused = true;
    emit({ micActive: true, micPaused: true, micLevel: 0 });
  }

  function resume(): void {
    if (!stream || !ingressSocket?.isConnected() || !paused) return;
    paused = false;
    emit({ micActive: true, micPaused: false });
  }

  async function stop(): Promise<void> {
    if (raf !== null) cancelAnimationFrame(raf);
    raf = null;
    workletNode?.port.close();
    workletNode?.disconnect();
    workletNode = null;
    silentGain?.disconnect();
    silentGain = null;
    source?.disconnect();
    source = null;
    analyser?.disconnect();
    analyser = null;
    stream?.getTracks().forEach((track) => track.stop());
    stream = null;
    ingressSocket?.stop();
    ingressSocket = null;
    paused = false;
    if (audioContext && audioContext.state !== "closed") {
      await audioContext.close().catch(() => undefined);
    }
    audioContext = null;
    emit({
      micActive: false,
      micPaused: false,
      micLevel: 0,
      audioTransport: "disconnected",
      audioTransportError: null,
      sttTransport: "disabled",
      sttError: null,
      lastSttActivityAt: null,
      lastTranscriptAt: null,
      sampleRate: null,
    });
  }

  function startLevelLoop(): void {
    const data = new Uint8Array(analyser?.fftSize ?? 0);
    const tick = () => {
      if (!analyser || data.length === 0) return;
      if (paused) {
        emit({ micLevel: 0 });
        raf = requestAnimationFrame(tick);
        return;
      }
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      emit({ micLevel: Math.min(1, Math.sqrt(sum / data.length) * 2.2) });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  }

  function handleSocketMessage(data: unknown): void {
    if (typeof data !== "string") return;
    try {
      const message = JSON.parse(data) as {
        type?: string;
        error?: string | null;
        state?: unknown;
        at?: unknown;
        ts?: unknown;
      };
      if (message.type === "error") emitTransportError(message.error ?? "audio ingress error");
      if (message.type === "connected" && ingressSocket?.isConnected()) {
        emit({ audioTransport: "connected", audioTransportError: null });
      }
      if (message.type === "stt_status") {
        const sttTransport = parseSttTransport(message.state);
        if (sttTransport) {
          emit({
            sttTransport,
            sttError: typeof message.error === "string" ? message.error : null,
            lastSttActivityAt: parseTimestamp(message.at) ?? Date.now(),
          });
        }
      }
      if (message.type === "stt_transcript") {
        emit({
          sttTransport: snapshot.sttTransport === "disabled" ? "connected" : snapshot.sttTransport,
          lastTranscriptAt: parseTimestamp(message.ts) ?? Date.now(),
          lastSttActivityAt: Date.now(),
        });
      }
    } catch {
      /* Ignore non-protocol text frames. */
    }
  }

  function emitTransportError(message: string): void {
    emit({
      audioTransport: "error",
      audioTransportError: message,
      sttTransport: "error",
      sttError: message,
      micActive: false,
      micPaused: false,
    });
  }

  async function failCapture(message: string): Promise<void> {
    await stop();
    emitTransportError(message);
  }

  async function resolveSelectedDevice(deviceId: string | null): Promise<AudioInputDevice | null> {
    const devices = await listAudioInputDevices();
    return devices.find((device) => device.deviceId === deviceId) ?? devices[0] ?? null;
  }

  return {
    subscribe,
    start,
    pause,
    resume,
    stop,
    getSnapshot: () => snapshot,
  };
}

function initialSnapshot(): AudioCaptureSnapshot {
  return {
    micActive: false,
    micPaused: false,
    micLevel: 0,
    selectedInputDeviceId: null,
    selectedInputDeviceLabel: null,
    audioTransport: "disconnected",
    audioTransportError: null,
    sttTransport: "disabled",
    sttError: null,
    lastSttActivityAt: null,
    lastTranscriptAt: null,
    sampleRate: null,
  };
}

function parseSttTransport(value: unknown): SttTransportState | null {
  return value === "disabled" ||
    value === "connecting" ||
    value === "connected" ||
    value === "degraded" ||
    value === "error"
    ? value
    : null;
}

function parseTimestamp(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function ensureMediaDevices(): void {
  if (!navigator.mediaDevices?.getUserMedia || !navigator.mediaDevices.enumerateDevices) {
    throw new Error("browser audio input APIs are not available");
  }
}

function newAudioContext(): AudioContext {
  const AudioCtx =
    (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) throw new Error("Web Audio API is not available");
  return new AudioCtx();
}
